import type { LocalizedString } from "@repo/shared/types";
import {
  PDF_DEFAULT_PAGE_NUMBER_ALIGN,
  PDF_DEFAULT_PAGE_ORIENTATION,
  PDF_PAGE_NUMBER_ALIGNS,
  PDF_PAGE_ORIENTATIONS,
  type PdfPageNumberAlign,
  type PdfPageOrientation,
  type PdfSection,
  type PdfSectionScope,
} from "@repo/shared/types";
import { useEffect, useRef, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { postFormData } from "@/lib/api/_client";
import { useCreateSection, useUpdateSection } from "@/lib/hooks/use-pdf-templates";
import { usePdfTemplateQuizzes } from "@/lib/hooks/use-quizzes";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./section-dialog.i18n";

type FlowField = {
  id: string;
  name: string;
  nameI18n?: LocalizedString | null;
};

type ClientCustomField = {
  id: string;
  name: string;
  nameI18n?: LocalizedString | null;
  type: string;
  isActive: boolean;
  isStandard?: boolean;
};

type SectionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  section?: PdfSection | null;
  isMultiGoalFlow: boolean;
  flowFields?: FlowField[];
  clientCustomFields?: ClientCustomField[];
};

type BackgroundType = "none" | "color" | "image";
type Alignment = "left" | "center" | "right";

function toPageNumberAlign(value: string | null | undefined): PdfPageNumberAlign {
  return (PDF_PAGE_NUMBER_ALIGNS as readonly string[]).includes(value ?? "")
    ? (value as PdfPageNumberAlign)
    : PDF_DEFAULT_PAGE_NUMBER_ALIGN;
}

function toPageOrientation(value: string | null | undefined): PdfPageOrientation {
  return (PDF_PAGE_ORIENTATIONS as readonly string[]).includes(value ?? "")
    ? (value as PdfPageOrientation)
    : PDF_DEFAULT_PAGE_ORIENTATION;
}

// Helper to get image preview URL from S3 key
const getImagePreviewUrl = (s3Key: string | null | undefined): string | null => {
  if (!s3Key) return null;
  return `${process.env.BUN_PUBLIC_API_URL}/internal/uploads/images/${s3Key}`;
};

export function SectionDialog({
  open,
  onOpenChange,
  templateId,
  section,
  isMultiGoalFlow,
  flowFields = [],
  clientCustomFields = [],
}: SectionDialogProps) {
  const localize = useLocalize();
  const t = useT(T);
  const bgFileInputRef = useRef<HTMLInputElement>(null);
  const logoFileInputRef = useRef<HTMLInputElement>(null);
  const headerTextRef = useRef<HTMLInputElement>(null);
  const createSectionMutation = useCreateSection();
  const updateSectionMutation = useUpdateSection();
  const [backgroundType, setBackgroundType] = useState<BackgroundType>("none");
  const [formData, setFormData] = useState({
    name: "",
    scope: "global" as PdfSectionScope,
    bgColor: "",
    bgImage: "",
    bgImageType: "" as "" | "png" | "jpg" | "pdf",
    // Header
    headerText: "",
    headerTextAlign: "left" as Alignment,
    headerLogo: "",
    headerLogoType: "" as "" | "svg" | "png" | "jpg",
    headerLogoAlign: "right" as Alignment,
    headerShowLine: false,
    headerLineColor: "#000000",
    // Page
    orientation: PDF_DEFAULT_PAGE_ORIENTATION as PdfPageOrientation,
    // Footer
    footerShowNumbers: false,
    footerPageNumberAlign: PDF_DEFAULT_PAGE_NUMBER_ALIGN as PdfPageNumberAlign,
    footerShowLine: false,
    footerLineColor: "#000000",
  });

  const isEditing = !!section;

  // Reset form when dialog opens/closes or section changes
  useEffect(() => {
    if (open) {
      if (section) {
        // Determine background type based on existing data
        let bgType: BackgroundType = "none";
        if (section.bgImage) {
          bgType = "image";
        } else if (section.bgColor) {
          bgType = "color";
        }
        setBackgroundType(bgType);
        setFormData({
          name: section.name,
          scope: section.scope as PdfSectionScope,
          bgColor: section.bgColor || "",
          bgImage: section.bgImage || "",
          bgImageType: (section.bgImageType as "" | "png" | "jpg" | "pdf") || "",
          // Header
          headerText: section.headerText || "",
          headerTextAlign: (section.headerTextAlign as Alignment) || "left",
          headerLogo: section.headerLogo || "",
          headerLogoType: (section.headerLogoType as "" | "svg" | "png" | "jpg") || "",
          headerLogoAlign: (section.headerLogoAlign as Alignment) || "right",
          headerShowLine: section.headerShowLine || false,
          headerLineColor: section.headerLineColor || "#000000",
          orientation: toPageOrientation(section.orientation),
          // Footer
          footerShowNumbers: section.footerShowNumbers || false,
          footerPageNumberAlign: toPageNumberAlign(section.footerPageNumberAlign),
          footerShowLine: section.footerShowLine || false,
          footerLineColor: section.footerLineColor || "#000000",
        });
      } else {
        setBackgroundType("none");
        setFormData({
          name: "",
          scope: "global",
          bgColor: "",
          bgImage: "",
          bgImageType: "",
          headerText: "",
          headerTextAlign: "left",
          headerLogo: "",
          headerLogoType: "",
          headerLogoAlign: "right",
          headerShowLine: false,
          headerLineColor: "#000000",
          orientation: PDF_DEFAULT_PAGE_ORIENTATION,
          footerShowNumbers: false,
          footerPageNumberAlign: PDF_DEFAULT_PAGE_NUMBER_ALIGN,
          footerShowLine: false,
          footerLineColor: "#000000",
        });
      }
    }
  }, [open, section]);

  // Handle file upload for background image
  const handleBgFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    const validTypes = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      toast.error(t.invalidFileType, {
        description: t.supportedFormatsImage,
      });
      return;
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(t.fileTooLarge, {
        description: t.maxFileSize,
      });
      return;
    }

    // Upload to S3
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    uploadFormData.append("type", "background");

    try {
      const result = await postFormData<{ s3Key: string; imageType: string }>(
        "/internal/uploads/upload-pdf-image",
        uploadFormData,
      );
      setFormData({
        ...formData,
        bgImage: result.s3Key,
        bgImageType: result.imageType as "png" | "jpg" | "pdf",
      });
    } catch (err: any) {
      toast.error(t.error, { description: err.message || "Upload failed" });
    }
  };

  // Handle file upload for logo
  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    const validTypes = ["image/svg+xml", "image/png", "image/jpeg", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast.error(t.invalidFileType, {
        description: t.supportedFormatsLogo,
      });
      return;
    }

    // Check file size (max 2MB for logos)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(t.fileTooLarge, {
        description: t.maxLogoSize,
      });
      return;
    }

    // Upload to S3
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    uploadFormData.append("type", "logo");

    try {
      const result = await postFormData<{ s3Key: string; imageType: string }>(
        "/internal/uploads/upload-pdf-image",
        uploadFormData,
      );
      setFormData({
        ...formData,
        headerLogo: result.s3Key,
        headerLogoType: result.imageType as "svg" | "png" | "jpg",
      });
    } catch (err: any) {
      toast.error(t.error, { description: err.message || "Upload failed" });
    }
  };

  // QL-13 — `{{Quiz.<id>}}` renders the client's result for that quiz.
  const { quizzes: activeQuizzes } = usePdfTemplateQuizzes(templateId);

  // Insert field placeholder at cursor position
  const insertField = (fieldPlaceholder: string) => {
    const input = headerTextRef.current;
    if (!input) return;

    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const currentValue = formData.headerText || "";

    const newValue = `${currentValue.substring(0, start)}{{${fieldPlaceholder}}}${currentValue.substring(end)}`;

    setFormData({ ...formData, headerText: newValue });

    // Set cursor position after inserted text
    requestAnimationFrame(() => {
      input.focus();
      const newPos = start + fieldPlaceholder.length + 4;
      input.setSelectionRange(newPos, newPos);
    });
  };

  // Client fields (always available)
  const clientFields = [
    { label: "Name", value: "Client.Name" },
    { label: "Type", value: "Client.Type" },
    { label: "Email", value: "Client.Email" },
    { label: "Mobile Phone", value: "Client.MobilePhone" },
    { label: "SSN", value: "Client.SSN" },
    { label: "Org ID", value: "Client.OrgId" },
    { label: "Country", value: "Client.Country" },
  ];

  // Owner fields (advisor / client owner data)
  const ownerFields = [
    { label: "Name", value: "Owner.Name" },
    { label: "Email", value: "Owner.Email" },
    { label: "Phone Number", value: "Owner.Phone" },
    { label: "Title", value: "Owner.Title" },
    { label: "Signature", value: "Owner.Signature" },
  ];

  // Flow fields (flow instance data)
  const flowInstanceFields = [
    { label: "Name", value: "Flow.Name" },
    { label: "Cancel Reason", value: "Flow.CancelReason" },
    { label: "Expecting Financial Changes", value: "Flow.ExpectingFinancialChanges" },
    { label: "Expecting Financial Changes Note", value: "Flow.ExpectingFinancialChangesNote" },
  ];

  // Document fields (always available)
  const documentFields = [{ label: "Today", value: "today" }];

  // Goal fields (only available in per_goal scope)
  const goalFields = [
    { label: "Goal Name", value: "Goal Name" },
    { label: "Goal Type", value: "Goal Type" },
    { label: "Goal Description", value: "Goal Description" },
    { label: "Risk Score", value: "Risk Score" },
    // QL-375 group 10 — the goal's mandate (empty on a goal without one)
    { label: "Mandate Name", value: "Mandate Name" },
    { label: "Mandate Status", value: "Mandate Status" },
    { label: "Existing Mandate Name", value: "Existing Mandate Name" },
  ];

  const isPerGoalScope = section?.scope === "per_goal";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error(t.error, {
        description: t.validationSectionNameRequired,
      });
      return;
    }

    try {
      // Prepare data based on background type (mutually exclusive)
      const bgColor = backgroundType === "color" ? formData.bgColor || null : null;
      const bgImage = backgroundType === "image" ? formData.bgImage || null : null;
      const bgImageType = backgroundType === "image" ? formData.bgImageType || null : null;

      if (isEditing && section) {
        await updateSectionMutation.mutateAsync({
          sectionId: section.id,
          data: {
            name: formData.name,
            orientation: formData.orientation,
            bgColor,
            bgImage,
            bgImageType,
            headerText: formData.headerText || null,
            headerTextAlign: formData.headerText ? formData.headerTextAlign : null,
            headerLogo: formData.headerLogo || null,
            headerLogoType: formData.headerLogo ? formData.headerLogoType : null,
            headerLogoAlign: formData.headerLogo ? formData.headerLogoAlign : null,
            headerShowLine: formData.headerShowLine,
            headerLineColor: formData.headerShowLine ? formData.headerLineColor : null,
            footerShowNumbers: formData.footerShowNumbers,
            footerPageNumberAlign: formData.footerPageNumberAlign,
            footerShowLine: formData.footerShowLine,
            footerLineColor: formData.footerShowLine ? formData.footerLineColor : null,
          },
        });
      } else {
        await createSectionMutation.mutateAsync({
          templateId,
          // The scope Select only offers global / per_goal; a per_goal create auto-adds its
          // bonded per_portfolio sub-node server-side.
          data: { name: formData.name, scope: formData.scope as "global" | "per_goal" },
        });
      }
      toast.success(section ? t.sectionUpdatedSuccess : t.sectionCreatedSuccess);
      onOpenChange(false);
    } catch (_error) {
      toast.error(t.error, {
        description: t.unexpectedError,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? `${t.sectionSettings} (${section.scope === "global" ? t.global : section.scope === "per_portfolio" ? t.perPortfolio : t.perGoal})`
              : t.addNewSection}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t.templateSettingsDescription : t.createSectionDescription}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4 px-1 -mx-1 max-h-[60vh] overflow-y-auto">
            {/* Section Name */}
            <div className="space-y-2">
              <Label htmlFor="section-name">
                {t.sectionName} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="section-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t.sectionNamePlaceholder}
                required
                disabled={createSectionMutation.isPending || updateSectionMutation.isPending}
              />
            </div>

            {/* Section Scope - only on create */}
            {!isEditing && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="scope">
                    {t.sectionScope} <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.scope}
                    onValueChange={(value) =>
                      setFormData({ ...formData, scope: value as PdfSectionScope })
                    }
                    disabled={createSectionMutation.isPending || updateSectionMutation.isPending}
                  >
                    <SelectTrigger id="scope">
                      <SelectValue placeholder={t.sectionScope} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">{t.globalScopeOption}</SelectItem>
                      <SelectItem value="per_goal">
                        {isMultiGoalFlow ? t.perGoalScopeOptionMulti : t.perGoalScopeOptionSingle}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    {formData.scope === "global"
                      ? t.globalScopeDescription
                      : isMultiGoalFlow
                        ? t.perGoalScopeDescriptionMulti
                        : t.perGoalScopeDescriptionSingle}
                    {/* Goal sections include a bonded portfolio sub-node — see the section list. */}
                  </p>
                </div>

                {/* Info for per_goal scope in single-goal flows */}
                {!isMultiGoalFlow && formData.scope === "per_goal" && (
                  <div className="rounded-md bg-info/10 border border-info/30 p-3 text-sm text-muted-foreground">
                    <strong>{t.noteLabel}</strong> {t.perGoalSingleNote}
                  </div>
                )}
              </>
            )}

            {/* Page Styling - only on edit */}
            {isEditing && (
              <Tabs defaultValue="page" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="page">{t.page}</TabsTrigger>
                  <TabsTrigger value="background">{t.background}</TabsTrigger>
                  <TabsTrigger value="header">{t.header}</TabsTrigger>
                  <TabsTrigger value="footer">{t.footer}</TabsTrigger>
                </TabsList>

                <TabsContent value="page" className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>{t.orientation}</Label>
                    <RadioGroup
                      value={formData.orientation}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          orientation: toPageOrientation(value),
                        })
                      }
                      className="flex gap-4"
                      disabled={createSectionMutation.isPending || updateSectionMutation.isPending}
                    >
                      <OptionRow>
                        <RadioGroupItem value="portrait" id="orientation-portrait" />
                        <Label
                          htmlFor="orientation-portrait"
                          className="font-normal cursor-pointer"
                        >
                          {t.portrait}
                        </Label>
                      </OptionRow>
                      <OptionRow>
                        <RadioGroupItem value="landscape" id="orientation-landscape" />
                        <Label
                          htmlFor="orientation-landscape"
                          className="font-normal cursor-pointer"
                        >
                          {t.landscape}
                        </Label>
                      </OptionRow>
                    </RadioGroup>
                    <p className="text-sm text-muted-foreground">{t.orientationHelp}</p>
                  </div>
                </TabsContent>

                <TabsContent value="background" className="space-y-4 pt-4">
                  {/* Background Type Selection */}
                  <RadioGroup
                    value={backgroundType}
                    onValueChange={(value) => setBackgroundType(value as BackgroundType)}
                    className="flex gap-4"
                    disabled={createSectionMutation.isPending || updateSectionMutation.isPending}
                  >
                    <OptionRow>
                      <RadioGroupItem value="none" id="bg-none" />
                      <Label htmlFor="bg-none" className="font-normal cursor-pointer">
                        {t.noneWhite}
                      </Label>
                    </OptionRow>
                    <OptionRow>
                      <RadioGroupItem value="color" id="bg-color" />
                      <Label htmlFor="bg-color" className="font-normal cursor-pointer">
                        {t.color}
                      </Label>
                    </OptionRow>
                    <OptionRow>
                      <RadioGroupItem value="image" id="bg-image" />
                      <Label htmlFor="bg-image" className="font-normal cursor-pointer">
                        {t.image}
                      </Label>
                    </OptionRow>
                  </RadioGroup>

                  {/* Background Color Picker */}
                  {backgroundType === "color" && (
                    <div className="space-y-2">
                      <Label>{t.backgroundColor}</Label>
                      <div className="flex items-center gap-3">
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="w-10 h-10 rounded-lg border-2 border-input cursor-pointer hover:ring-2 hover:ring-ring hover:ring-offset-2 transition-all shadow-sm"
                              style={{
                                backgroundColor: formData.bgColor || "#ffffff",
                              }}
                              disabled={
                                createSectionMutation.isPending || updateSectionMutation.isPending
                              }
                            />
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-3" align="start">
                            <HexColorPicker
                              color={formData.bgColor || "#ffffff"}
                              onChange={(color) => setFormData({ ...formData, bgColor: color })}
                            />
                            <Input
                              type="text"
                              value={formData.bgColor}
                              onChange={(e) =>
                                setFormData({ ...formData, bgColor: e.target.value })
                              }
                              placeholder="#ffffff"
                              className="mt-2 w-full font-mono text-sm"
                              disabled={
                                createSectionMutation.isPending || updateSectionMutation.isPending
                              }
                            />
                          </PopoverContent>
                        </Popover>
                        <span className="font-mono text-sm text-muted-foreground">
                          {formData.bgColor || "#ffffff"}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Background Image Upload */}
                  {backgroundType === "image" && (
                    <div className="space-y-2">
                      <Label htmlFor="bg-image-upload">{t.backgroundImage}</Label>
                      <Input
                        id="bg-image-upload"
                        ref={bgFileInputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.pdf"
                        onChange={handleBgFileChange}
                        disabled={
                          createSectionMutation.isPending || updateSectionMutation.isPending
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        {t.backgroundImageDescription}
                      </p>
                      {formData.bgImage && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="text-xs text-success">
                              {t.imageUploaded} ({formData.bgImageType?.toUpperCase()})
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setFormData({ ...formData, bgImage: "", bgImageType: "" });
                                if (bgFileInputRef.current) {
                                  bgFileInputRef.current.value = "";
                                }
                              }}
                              disabled={
                                createSectionMutation.isPending || updateSectionMutation.isPending
                              }
                            >
                              {t.remove}
                            </Button>
                          </div>
                          {/* Preview for image files (not PDF) */}
                          {formData.bgImageType !== "pdf" &&
                            getImagePreviewUrl(formData.bgImage) && (
                              <div className="border rounded-md p-2 bg-white max-w-[200px]">
                                <img
                                  src={getImagePreviewUrl(formData.bgImage)!}
                                  alt={t.backgroundPreview}
                                  className="w-full h-auto object-contain"
                                  style={{ maxHeight: "150px" }}
                                />
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="header" className="space-y-4 pt-4">
                  {/* Header Text */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="header-text" className="text-xs text-muted-foreground">
                        {t.textLabel}
                      </Label>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="h-7 text-xs">
                            {t.insertField}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-48 max-h-[400px] overflow-y-auto"
                        >
                          <DropdownMenuLabel>{t.client}</DropdownMenuLabel>
                          <DropdownMenuGroup>
                            {clientFields.map((field) => (
                              <DropdownMenuItem
                                key={field.value}
                                onClick={() => insertField(field.value)}
                              >
                                {field.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuGroup>
                          {(() => {
                            const customOnly = clientCustomFields.filter(
                              (f) => f.isActive && !f.isStandard,
                            );
                            return customOnly.length > 0 ? (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel>{t.customFields}</DropdownMenuLabel>
                                <DropdownMenuGroup>
                                  {customOnly.map((field) => {
                                    // Use ID-based placeholder for custom fields
                                    const displayName = localize(field.nameI18n) || field.name;
                                    return (
                                      <DropdownMenuItem
                                        key={field.id}
                                        onClick={() => insertField(`Client.${field.id}`)}
                                      >
                                        {displayName}
                                      </DropdownMenuItem>
                                    );
                                  })}
                                </DropdownMenuGroup>
                              </>
                            ) : null;
                          })()}
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel>{t.ownerLabel}</DropdownMenuLabel>
                          <DropdownMenuGroup>
                            {ownerFields.map((field) => (
                              <DropdownMenuItem
                                key={field.value}
                                onClick={() => insertField(field.value)}
                              >
                                {field.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuGroup>
                          {isPerGoalScope && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel>{t.goalLabel}</DropdownMenuLabel>
                              <DropdownMenuGroup>
                                {goalFields.map((field) => (
                                  <DropdownMenuItem
                                    key={field.value}
                                    onClick={() => insertField(field.value)}
                                  >
                                    {field.label}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuGroup>
                            </>
                          )}
                          {flowFields.length > 0 && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel>{t.flowFieldsLabel}</DropdownMenuLabel>
                              <DropdownMenuGroup>
                                {flowFields.map((field) => {
                                  // Use ID-based placeholder for flow fields
                                  const displayName = field.name;
                                  return (
                                    <DropdownMenuItem
                                      key={field.id}
                                      onClick={() => insertField(`Flow.${field.id}`)}
                                    >
                                      {displayName}
                                    </DropdownMenuItem>
                                  );
                                })}
                              </DropdownMenuGroup>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel>{t.flowLabel}</DropdownMenuLabel>
                          <DropdownMenuGroup>
                            {flowInstanceFields.map((field) => (
                              <DropdownMenuItem
                                key={field.value}
                                onClick={() => insertField(field.value)}
                              >
                                {field.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuGroup>
                          {activeQuizzes.length > 0 && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel>{t.quizFields}</DropdownMenuLabel>
                              <DropdownMenuGroup>
                                {activeQuizzes.map((quiz) => (
                                  <DropdownMenuItem
                                    key={quiz.id}
                                    onClick={() => insertField(`Quiz.${quiz.id}`)}
                                  >
                                    {quiz.name}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuGroup>
                              <DropdownMenuLabel>{t.quizOverrideFields}</DropdownMenuLabel>
                              <DropdownMenuGroup>
                                {activeQuizzes.map((quiz) => (
                                  <DropdownMenuItem
                                    key={quiz.id}
                                    onClick={() => insertField(`Quiz.${quiz.id}.Override`)}
                                  >
                                    {quiz.name}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuGroup>
                              <DropdownMenuLabel>{t.quizExternalFields}</DropdownMenuLabel>
                              <DropdownMenuGroup>
                                {activeQuizzes.map((quiz) => (
                                  <DropdownMenuItem
                                    key={quiz.id}
                                    onClick={() => insertField(`Quiz.${quiz.id}.External`)}
                                  >
                                    {quiz.name}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuGroup>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel>{t.documentLabel}</DropdownMenuLabel>
                          <DropdownMenuGroup>
                            {documentFields.map((field) => (
                              <DropdownMenuItem
                                key={field.value}
                                onClick={() => insertField(field.value)}
                              >
                                {field.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        id="header-text"
                        ref={headerTextRef}
                        value={formData.headerText}
                        onChange={(e) => setFormData({ ...formData, headerText: e.target.value })}
                        placeholder={t.headerTextPlaceholder}
                        disabled={
                          createSectionMutation.isPending || updateSectionMutation.isPending
                        }
                        className="flex-1"
                      />
                      <Select
                        value={formData.headerTextAlign}
                        onValueChange={(value) => {
                          const newAlign = value as Alignment;
                          // If logo exists and would be on same side, flip logo to opposite
                          const newLogoAlign =
                            formData.headerLogo && formData.headerLogoAlign === newAlign
                              ? newAlign === "left"
                                ? "right"
                                : "left"
                              : formData.headerLogoAlign;
                          setFormData({
                            ...formData,
                            headerTextAlign: newAlign,
                            headerLogoAlign: newLogoAlign,
                          });
                        }}
                        disabled={
                          createSectionMutation.isPending || updateSectionMutation.isPending
                        }
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="left">{t.left}</SelectItem>
                          <SelectItem value="right">{t.right}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Header Logo */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">{t.logo}</Label>
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 space-y-2">
                        <Input
                          id="header-logo-upload"
                          ref={logoFileInputRef}
                          type="file"
                          accept=".svg,.png,.jpg,.jpeg"
                          onChange={handleLogoFileChange}
                          disabled={
                            createSectionMutation.isPending || updateSectionMutation.isPending
                          }
                        />
                        {formData.headerLogo && getImagePreviewUrl(formData.headerLogo) && (
                          <div className="flex items-center gap-2">
                            <div className="border rounded p-1 bg-white">
                              <img
                                src={getImagePreviewUrl(formData.headerLogo)!}
                                alt={t.logoPreview}
                                className="h-8 w-auto object-contain"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setFormData({ ...formData, headerLogo: "", headerLogoType: "" });
                                if (logoFileInputRef.current) {
                                  logoFileInputRef.current.value = "";
                                }
                              }}
                              disabled={
                                createSectionMutation.isPending || updateSectionMutation.isPending
                              }
                            >
                              {t.remove}
                            </Button>
                          </div>
                        )}
                      </div>
                      <Select
                        value={formData.headerLogoAlign}
                        onValueChange={(value) => {
                          const newAlign = value as Alignment;
                          // If text exists and would be on same side, flip text to opposite
                          const newTextAlign =
                            formData.headerText && formData.headerTextAlign === newAlign
                              ? newAlign === "left"
                                ? "right"
                                : "left"
                              : formData.headerTextAlign;
                          setFormData({
                            ...formData,
                            headerLogoAlign: newAlign,
                            headerTextAlign: newTextAlign,
                          });
                        }}
                        disabled={
                          createSectionMutation.isPending ||
                          updateSectionMutation.isPending ||
                          !formData.headerLogo
                        }
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="left">{t.left}</SelectItem>
                          <SelectItem value="right">{t.right}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-xs text-muted-foreground">{t.logoFileDescription}</p>
                  </div>

                  {/* Header Line */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="header-show-line"
                        checked={formData.headerShowLine}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, headerShowLine: checked === true })
                        }
                        disabled={
                          createSectionMutation.isPending || updateSectionMutation.isPending
                        }
                      />
                      <Label htmlFor="header-show-line" className="font-normal cursor-pointer">
                        {t.showLineBelowHeader}
                      </Label>
                    </div>
                    {formData.headerShowLine && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="w-6 h-6 rounded border border-input cursor-pointer"
                            style={{ backgroundColor: formData.headerLineColor }}
                            disabled={
                              createSectionMutation.isPending || updateSectionMutation.isPending
                            }
                          />
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-3" align="start">
                          <HexColorPicker
                            color={formData.headerLineColor}
                            onChange={(color) =>
                              setFormData({ ...formData, headerLineColor: color })
                            }
                          />
                          <Input
                            type="text"
                            value={formData.headerLineColor}
                            onChange={(e) =>
                              setFormData({ ...formData, headerLineColor: e.target.value })
                            }
                            placeholder="#000000"
                            className="mt-2 w-full font-mono text-sm"
                            disabled={
                              createSectionMutation.isPending || updateSectionMutation.isPending
                            }
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="footer" className="space-y-4 pt-4">
                  {/* Page Numbers */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="footer-show-numbers"
                        checked={formData.footerShowNumbers}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, footerShowNumbers: checked === true })
                        }
                        disabled={
                          createSectionMutation.isPending || updateSectionMutation.isPending
                        }
                      />
                      <Label htmlFor="footer-show-numbers" className="font-normal cursor-pointer">
                        {t.showPageNumbers}
                      </Label>
                    </div>
                    {formData.footerShowNumbers && (
                      <Select
                        value={formData.footerPageNumberAlign}
                        onValueChange={(value) =>
                          setFormData({
                            ...formData,
                            footerPageNumberAlign: toPageNumberAlign(value),
                          })
                        }
                        disabled={
                          createSectionMutation.isPending || updateSectionMutation.isPending
                        }
                      >
                        <SelectTrigger className="w-32" aria-label={t.pageNumberPlacement}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="left">{t.left}</SelectItem>
                          <SelectItem value="center">{t.center}</SelectItem>
                          <SelectItem value="right">{t.right}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Footer Line */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="footer-show-line"
                        checked={formData.footerShowLine}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, footerShowLine: checked === true })
                        }
                        disabled={
                          createSectionMutation.isPending || updateSectionMutation.isPending
                        }
                      />
                      <Label htmlFor="footer-show-line" className="font-normal cursor-pointer">
                        {t.showLineAboveFooter}
                      </Label>
                    </div>
                    {formData.footerShowLine && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="w-6 h-6 rounded border border-input cursor-pointer"
                            style={{ backgroundColor: formData.footerLineColor }}
                            disabled={
                              createSectionMutation.isPending || updateSectionMutation.isPending
                            }
                          />
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-3" align="start">
                          <HexColorPicker
                            color={formData.footerLineColor}
                            onChange={(color) =>
                              setFormData({ ...formData, footerLineColor: color })
                            }
                          />
                          <Input
                            type="text"
                            value={formData.footerLineColor}
                            onChange={(e) =>
                              setFormData({ ...formData, footerLineColor: e.target.value })
                            }
                            placeholder="#000000"
                            className="mt-2 w-full font-mono text-sm"
                            disabled={
                              createSectionMutation.isPending || updateSectionMutation.isPending
                            }
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </div>

          <DialogFooter>
            <DialogCancel
              disabled={createSectionMutation.isPending || updateSectionMutation.isPending}
            >
              {t.cancel}
            </DialogCancel>
            <Button
              type="submit"
              loading={createSectionMutation.isPending || updateSectionMutation.isPending}
            >
              {isEditing ? t.save : t.addSection}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
